import type { RefObject } from "react";
import { useCallback, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / k ** i).toFixed(1)} ${sizes[i]}`;
}

export interface FormViewProps {
  inputMode: "pdf" | "url";
  setInputMode: (v: "pdf" | "url") => void;
  linkedinUrl: string;
  setLinkedinUrl: (v: string) => void;
  pdfFile: File | null;
  setPdfFile: (v: File | null) => void;
  hasReferral: boolean;
  setHasReferral: (v: boolean) => void;
  refFrom: string;
  setRefFrom: (v: string) => void;
  refRole: string;
  setRefRole: (v: string) => void;
  refNote: string;
  setRefNote: (v: string) => void;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  submitting: boolean;
  discipline: string;
  setDiscipline: (v: string) => void;
  positionContext: string;
  setPositionContext: (v: string) => void;
  frameworks: Array<{ slug: string; name: string; levels: string[] }>;
}

export function FormView(props: FormViewProps) {
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file?.name.toLowerCase().endsWith(".pdf")) {
        props.setPdfFile(file);
      }
    },
    [props.setPdfFile],
  );

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-narrow fade-up" style={{ width: "100%" }}>
        <div className="card">
          <div className="card-head">
            <div className="card-head-left">
              <h2>Perfil do candidato</h2>
              <div className="sub">Origem dos dados que vamos analisar.</div>
            </div>
            <div className="card-head-right">
              <div className="seg" role="tablist" aria-label="Fonte do perfil">
                <button
                  type="button"
                  role="tab"
                  aria-selected={props.inputMode === "url"}
                  className={cn(props.inputMode === "url" && "active")}
                  onClick={() => props.setInputMode("url")}
                  data-testid="labs-ci-mode-url"
                >
                  URL do LinkedIn
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={props.inputMode === "pdf"}
                  className={cn(props.inputMode === "pdf" && "active")}
                  onClick={() => props.setInputMode("pdf")}
                  data-testid="labs-ci-mode-pdf"
                >
                  PDF do LinkedIn
                </button>
              </div>
            </div>
          </div>

          <div className="card-body padded">
            {props.inputMode === "pdf" && (
              <div className="field">
                <Label className="field-label" htmlFor="labs-ci-pdf-input">
                  PDF do perfil LinkedIn
                </Label>
                <input
                  id="labs-ci-pdf-input"
                  ref={props.fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={props.onFileChange}
                  data-testid="labs-ci-pdf-input"
                  style={{ display: "none" }}
                />
                {props.pdfFile ? (
                  <div
                    className="file-chip fade-up"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="name">{props.pdfFile.name}</div>
                      <div
                        className="meta"
                        style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}
                      >
                        <span className="pill forest no-dot">Anexado</span>
                        <span>{formatFileSize(props.pdfFile.size)} · PDF</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => props.setPdfFile(null)}
                      data-testid="labs-ci-pdf-remove"
                      aria-label="Remover arquivo"
                    >
                      Remover
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => props.fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cn("dropzone", dragOver && "over")}
                    data-testid="labs-ci-pdf-pick"
                  >
                    <div className="dz-title">
                      {dragOver ? "Solte o arquivo aqui" : "Arrastar PDF ou clicar para selecionar"}
                    </div>
                    <div className="dz-sub">Perfil LinkedIn exportado em PDF · máx. 10 MB</div>
                  </button>
                )}
              </div>
            )}

            {props.inputMode === "url" && (
              <div className="field">
                <Label htmlFor="linkedin-url" className="field-label">
                  Perfil do LinkedIn
                </Label>
                <Input
                  id="linkedin-url"
                  value={props.linkedinUrl}
                  onChange={(e) => props.setLinkedinUrl(e.target.value)}
                  placeholder="linkedin.com/in/joaocandidato"
                  data-testid="labs-ci-linkedin"
                  autoFocus
                />
                <p className="field-helper">
                  Cole o link completo ou apenas o username (ex.: joaocandidato).
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-head">
            <div className="card-head-left">
              <p
                className="eyebrow"
                style={{
                  font: "600 11px/1.4 'Inter', sans-serif",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "var(--r-ink-muted)",
                  margin: "0 0 4px",
                }}
              >
                Posição-alvo
              </p>
              <h2>Contra qual framework avaliar</h2>
              <div className="sub">
                O nível é recomendado automaticamente pelo modelo a partir do perfil.
              </div>
            </div>
          </div>
          <div className="card-body padded">
            <div className="field">
              <Label htmlFor="discipline" className="field-label">
                Disciplina
              </Label>
              <Select value={props.discipline} onValueChange={props.setDiscipline}>
                <SelectTrigger id="discipline">
                  <SelectValue placeholder="Selecionar disciplina" />
                </SelectTrigger>
                <SelectContent>
                  {props.frameworks.map((f) => (
                    <SelectItem key={f.slug} value={f.slug}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label htmlFor="position-context" className="field-label">
                Contexto adicional{" "}
                <span style={{ fontWeight: 400, color: "var(--r-ink-muted)" }}>(opcional)</span>
              </Label>
              <Input
                id="position-context"
                value={props.positionContext}
                onChange={(e) => props.setPositionContext(e.target.value)}
                placeholder="Ex.: foco em stakeholder management, time de pagamentos"
              />
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-head">
            <div className="card-head-left">
              <h2>Indicação interna</h2>
              <div className="sub">Candidato foi indicado por alguém da empresa?</div>
            </div>
            <div className="card-head-right">
              <Switch
                checked={props.hasReferral}
                onCheckedChange={props.setHasReferral}
                data-testid="labs-ci-referral-toggle"
              />
            </div>
          </div>
          <div
            aria-hidden={!props.hasReferral}
            className={cn(
              "grid transition-all duration-300 ease-out",
              props.hasReferral ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
            )}
          >
            <div className="overflow-hidden">
              <div className="card-body padded">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="field">
                    <Label htmlFor="referral-from" className="field-label">
                      Indicado por
                    </Label>
                    <Input
                      id="referral-from"
                      tabIndex={props.hasReferral ? 0 : -1}
                      value={props.refFrom}
                      onChange={(e) => props.setRefFrom(e.target.value)}
                      placeholder="Nome"
                      data-testid="labs-ci-ref-from"
                    />
                  </div>
                  <div className="field">
                    <Label htmlFor="referral-role" className="field-label">
                      Cargo · relação
                    </Label>
                    <Input
                      id="referral-role"
                      tabIndex={props.hasReferral ? 0 : -1}
                      value={props.refRole}
                      onChange={(e) => props.setRefRole(e.target.value)}
                      placeholder="Ex.: Diretor · Engenharia"
                      data-testid="labs-ci-ref-role"
                    />
                  </div>
                </div>
                <div className="field">
                  <Label htmlFor="referral-note" className="field-label">
                    Nota de indicação{" "}
                    <span style={{ fontWeight: 400, color: "var(--r-ink-muted)" }}>(opcional)</span>
                  </Label>
                  <Textarea
                    id="referral-note"
                    tabIndex={props.hasReferral ? 0 : -1}
                    rows={3}
                    value={props.refNote}
                    onChange={(e) => props.setRefNote(e.target.value)}
                    placeholder="O que disseram sobre este candidato?"
                    data-testid="labs-ci-ref-note"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={props.onSubmit}
            disabled={props.submitting || !props.discipline}
            data-testid="labs-ci-submit"
            className="btn primary"
            style={{ minWidth: 200, height: 40 }}
          >
            {props.submitting ? (
              <span>
                Avaliando
                <span className="ell">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </span>
            ) : (
              "Avaliar candidato"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
